'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { createDebug } from '@shared/debug';
import { useFormatter, useTranslations } from 'next-intl';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { getDepositLimitAmountMax, getDepositLimitSchemaForForm } from '../../../../DepositLimits/DepositLimitSchema';
import { MultistepForm } from '../../../../form-controls/MultistepForm/MultistepForm';
import { useStepTracking } from '../../../hooks/useStepTracking';
import { MyAccountSheet } from '../../../MyAccountSheet/MyAccountSheet';
import type { DepositLimit, LimitPeriod } from '../../types';
import { getTrackTypeForPeriod, getTranslationKeyForPeriod } from '../utils/utils';
import { SetDepositLimitStep } from './steps/SetDepositLimitStep';
import { SuccessStep } from './steps/SuccessStep';

const debug = createDebug({ isEnabled: false });

const SubmittedValueContext = createContext<{
  submittedValue: number | undefined;
  setSubmittedValue: (value: number) => void;
}>({
  submittedValue: undefined,
  setSubmittedValue: () => {},
});

const useSubmittedValue = () => useContext(SubmittedValueContext);

type OnLimitChangeHandler = (newLimit: number) => void;

type SetDepositLimitSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: { id: string; firstName: string };
  limitPeriod: LimitPeriod;
  onLimitChange?: OnLimitChangeHandler;
  limits: DepositLimit[];
  onSaving?: (isSaving: boolean) => void;
  trigger: React.ReactNode;
};

type DepositLimitsFormValue = {
  period: { label: string; value: number };
  amount: string;
};

type FormValues = {
  depositLimits: DepositLimitsFormValue;
};

function getPeriodForLimitPeriod(limitPeriod: LimitPeriod, t: (key: string) => string): { label: string; value: number } {
  const periodMap: Record<LimitPeriod, { label: string; value: number }> = {
    MONTH: { label: t('buttons.monthly'), value: 0 },
    WEEK: { label: t('buttons.weekly'), value: 1 },
    DAY: { label: t('buttons.daily'), value: 2 },
  };
  return periodMap[limitPeriod];
}

const SetDepositLimitSheet = ({ open, onOpenChange, user, limitPeriod, onLimitChange, limits, onSaving, trigger }: SetDepositLimitSheetProps) => {
  const t = useTranslations('my_account.safer_gambling.deposit_limits');
  const tForm = useTranslations('form');
  const tMyAccount = useTranslations('my_account');
  const formatter = useFormatter();
  const currentDepositLimit = limits.find(limit => limit.period === limitPeriod)?.limitValue || 0;
  const hasExistingLimit = currentDepositLimit > 0;

  const [persistedSubmittedValue, setPersistedSubmittedValue] = useState<number | undefined>(undefined);
  const [originalLimitAtOpen, setOriginalLimitAtOpen] = useState<number>(currentDepositLimit);
  const hasInitialized = useRef(false);

  const periodValue = useMemo(() => getPeriodForLimitPeriod(limitPeriod, tForm), [limitPeriod, tForm]);

  const periodKey = getTranslationKeyForPeriod(limitPeriod);
  const validationKey = `pages.set_deposit_limit.set_${periodKey}_deposit_limit_step`;
  const maxValidationKey = `${validationKey}.validation_max`;
  const depositLimitAmountMax = getDepositLimitAmountMax(limitPeriod);
  const formSchema = useMemo(
    () =>
      getDepositLimitSchemaForForm(tMyAccount, {
        requiredMessage: tMyAccount(`${validationKey}.validation`),
        rangeMessage: tMyAccount(`${validationKey}.validation`),
        maxMessage: tMyAccount.has(maxValidationKey) ? tMyAccount(maxValidationKey, { max: formatter.number(depositLimitAmountMax) }) : undefined,
        unchangedMessage: tMyAccount(`${validationKey}.validation_same_value`),
        originalLimitInCents: originalLimitAtOpen > 0 ? originalLimitAtOpen : undefined,
      }),
    [tMyAccount, validationKey, maxValidationKey, originalLimitAtOpen, depositLimitAmountMax, formatter]
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as never,
    mode: 'onBlur',
    defaultValues: {
      depositLimits: {
        period: periodValue,
        amount: currentDepositLimit > 0 ? String(currentDepositLimit / 100) : '',
      },
    },
  });

  useEffect(() => {
    if (open && !hasInitialized.current) {
      setOriginalLimitAtOpen(currentDepositLimit);
      hasInitialized.current = true;
    }
  }, [open, currentDepositLimit]);

  useEffect(() => {
    if (!open) {
      hasInitialized.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const currentLimit = limits.find(limit => limit.period === limitPeriod)?.limitValue || 0;
      const hasExistingLimitForReset = currentLimit > 0;

      form.reset({
        depositLimits: {
          period: periodValue,
          amount: hasExistingLimitForReset ? String(currentLimit / 100) : '',
        },
      });
    }
  }, [open, limitPeriod, limits, form, periodValue]);

  const originalHasExistingLimit = originalLimitAtOpen > 0;
  const variant = originalHasExistingLimit ? 'edit' : 'create';
  const hasOtherActiveLimits = limits.some(limit => limit.limitType === 'DEPOSIT' && limit.period !== limitPeriod && (limit.limitValue ?? 0) > 0);
  const formStepTrackContainer = variant === 'edit' ? 'Deposit Limit Edit' : 'Deposit Limit Set';
  const successTrackContainer = variant === 'create' && hasOtherActiveLimits ? 'Deposit Limit Multiple Confirmed' : 'Deposit Limit Confirmed';

  const handleComplete = () => {
    try {
      const submittedValue = persistedSubmittedValue;
      if (typeof submittedValue === 'number') {
        onSaving?.(true);
        onLimitChange?.(submittedValue);
      }
      handleClose();
    } catch (error) {
      debug.forceError('Error during deposit limit set:', error);
      onSaving?.(false);
      handleClose();
    }
  };

  const handleClose = () => {
    const currentLimit = limits.find(limit => limit.period === limitPeriod)?.limitValue || 0;
    const hasExistingLimitForReset = currentLimit > 0;

    form.reset({
      depositLimits: {
        period: periodValue,
        amount: hasExistingLimitForReset ? String(currentLimit / 100) : '',
      },
    });
    setPersistedSubmittedValue(undefined);
    onOpenChange(false);
  };

  const setSubmittedValue = (value: number) => {
    setPersistedSubmittedValue(value);
  };

  const contextValue = {
    submittedValue: persistedSubmittedValue,
    setSubmittedValue,
  };

  const steps = [
    {
      id: 'set-deposit-limit',
      component: <SetDepositLimitStep limitPeriod={limitPeriod} variant={variant} originalLimit={originalLimitAtOpen} depositLimits={{ limits }} />,
      schema: z.object({
        depositLimits: z.object({
          period: z.object({ label: z.string(), value: z.number() }),
          amount: z.string().min(1),
        }),
      }),
      'data-track-container': formStepTrackContainer,
    },
    {
      id: 'success',
      component: (
        <SuccessStep
          user={user}
          limitPeriod={limitPeriod}
          variant={variant}
          limits={limits}
          onClose={handleClose}
          originalLimit={originalLimitAtOpen}
          submittedValue={persistedSubmittedValue}
        />
      ),
      schema: z.any(),
      'data-track-container': successTrackContainer,
    },
  ];

  const { currentTrackContainer, onStepChange } = useStepTracking(steps);

  const titleKey = hasExistingLimit ? `edit_${getTranslationKeyForPeriod(limitPeriod)}_title` : `set_${getTranslationKeyForPeriod(limitPeriod)}_title`;

  return (
    <MyAccountSheet
      trigger={trigger}
      title={t(titleKey)}
      open={open}
      onOpenChange={onOpenChange}
      showBackButton={false}
      data-track-feature="My Account"
      data-track-container={currentTrackContainer}
      data-track-type={getTrackTypeForPeriod(limitPeriod)}
    >
      <SubmittedValueContext.Provider value={contextValue}>
        <FormProvider {...form}>
          <MultistepForm steps={steps} showProgressBar={false} showNavigation={false} onComplete={handleComplete} onStepChange={onStepChange} className="h-full px-6 py-8" />
        </FormProvider>
      </SubmittedValueContext.Provider>
    </MyAccountSheet>
  );
};

export { SetDepositLimitSheet, useSubmittedValue };
